import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowLeft,
  GripVertical,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import { Link } from 'react-router';
import { Tooltip } from '@/components/Tooltip/Tooltip';

import { scrollToAnchorTarget } from './scrollOffsets';
import { useActiveHeading } from './useActiveHeading';
import {
  getContentUnitAnchorId,
  type TocEntry,
} from './useComponent';
import {
  getSectionUnitCount,
} from './sectionActions/groupUnitsIntoSections';
import { SectionKebabMenu } from './sectionActions/SectionKebabMenu';
import { SelectionToolbar } from './sectionActions/SelectionToolbar';
import type { ContentSection } from './sectionActions/sectionTypes';
import { useSectionDragAndDrop } from './sectionActions/useSectionDragAndDrop';
import { useSectionSelection } from './sectionActions/useSectionSelection';
import styles from './NavigationPanel.module.css';

interface NavigationPanelProps {
  courseId: string;
  courseTitle?: string;
  homeHref?: string;
  homeLabel?: string;
  isOpen: boolean;
  onHomeClick?(): void;
  onSelectionActiveChange?(active: boolean): void;
  navReorder?: NavigationPanelNavReorderActions;
  onToggle(): void;
  sectionActions?: NavigationPanelSectionActions;
  tocEntries: TocEntry[];
}

interface NavigationPanelNavReorderEntry {
  id: string;
  sectionId: string;
  unitId: string;
}

interface NavigationPanelNavReorderSection {
  entries: NavigationPanelNavReorderEntry[];
  id: string;
}

interface NavigationPanelNavReorderActions {
  disabledReason?: string;
  isDragDisabled?: boolean;
  isEnabled: boolean;
  onReorderEntry(sectionId: string, activeUnitId: string, overUnitId: string): void;
  sections: NavigationPanelNavReorderSection[];
}

interface NavigationPanelSectionActions {
  actionDisabledReason?: string;
  isActionDisabled?: boolean;
  isDragDisabled?: boolean;
  onBeforeSelectMultiple?(): boolean;
  onCopySections(sections: ContentSection[]): void;
  onDeleteSections(sections: ContentSection[]): void;
  onReorderSection(activeSectionId: string, overSectionId: string): void;
  sections: ContentSection[];
  selectionResetKey?: number;
}

const DESKTOP_MEDIA_QUERY = '(min-width: 64rem)';

function isDesktopViewport() {
  return typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function'
    ? window.matchMedia(DESKTOP_MEDIA_QUERY).matches
    : true;
}

function getFocusableElements(container: HTMLElement | null) {
  if (!container) {
    return [];
  }

  return Array.from(
    container.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  );
}

interface SectionTocRowProps {
  actionDisabledReason: string;
  anchorId: string;
  isActionDisabled: boolean;
  isActive: boolean;
  isDragDisabled: boolean;
  isSelected: boolean;
  isSelectionActive: boolean;
  onCopy(): void;
  onDelete(): void;
  onSelectMultiple(): void;
  onToggleSelected(): void;
  onNavigate(anchorId: string): void;
  section: ContentSection;
}

function SectionTocRow({
  actionDisabledReason,
  anchorId,
  isActionDisabled,
  isActive,
  isDragDisabled,
  isSelected,
  isSelectionActive,
  onCopy,
  onDelete,
  onNavigate,
  onSelectMultiple,
  onToggleSelected,
  section,
}: SectionTocRowProps) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    disabled: isDragDisabled,
    id: section.id,
  });
  const unitCount = getSectionUnitCount(section);
  const rowStyle = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
  };

  return (
    <li
      className={styles.sectionActionItem}
      data-dragging={isDragging ? 'true' : 'false'}
      ref={setNodeRef}
      style={rowStyle}
    >
      <div className={styles.sectionActionRow}>
        {isSelectionActive ? (
          <label className={styles.sectionCheckboxLabel}>
            <input
              checked={isSelected}
              className={styles.sectionCheckbox}
              onChange={onToggleSelected}
              type="checkbox"
            />
            <span className={styles.visuallyHidden}>
              Select section {section.title}
            </span>
          </label>
        ) : (
          <Tooltip
            content={
              isDragDisabled
                ? 'Save or cancel the current unit edit first.'
                : `Reorder section ${section.title}`
            }
          >
            <span className={styles.sectionDragAnchor}>
              <button
                {...attributes}
                {...listeners}
                aria-label={`Reorder section ${section.title}`}
                className={styles.sectionDragHandle}
                disabled={isDragDisabled}
                type="button"
              >
                <GripVertical aria-hidden="true" size={18} strokeWidth={2} />
              </button>
            </span>
          </Tooltip>
        )}

        <a
          aria-current={isActive ? 'true' : undefined}
          className={styles.sectionActionLink}
          data-active={isActive ? 'true' : 'false'}
          href={`#${anchorId}`}
          onClick={(event) => {
            event.preventDefault();
            onNavigate(anchorId);
          }}
        >
          <span className={styles.sectionTitle}>{section.title}</span>
          <span className={styles.sectionMeta}>
            {unitCount === 1 ? '1 content unit' : `${unitCount} content units`}
          </span>
        </a>

        <SectionKebabMenu
          disabled={isActionDisabled || isSelectionActive}
          disabledReason={actionDisabledReason}
          onCopy={onCopy}
          onDelete={onDelete}
          onSelectMultiple={onSelectMultiple}
          sectionTitle={section.title}
        />
      </div>
    </li>
  );
}

interface StandardTocRowProps {
  actionDisabledReason?: string;
  disabledReason?: string;
  entry: TocEntry;
  entryLabel: string;
  isActionDisabled?: boolean;
  isActive: boolean;
  isDragDisabled: boolean;
  isSelected?: boolean;
  isSelectionActive?: boolean;
  onCopy?(): void;
  onDelete?(): void;
  onNavigate(anchorId: string): void;
  onSelectMultiple?(): void;
  onToggleSelected?(): void;
  reorderEntry?: NavigationPanelNavReorderEntry;
  section?: ContentSection;
}

function StandardTocRow({
  actionDisabledReason,
  disabledReason,
  entry,
  entryLabel,
  isActionDisabled = false,
  isActive,
  isDragDisabled,
  isSelected = false,
  isSelectionActive = false,
  onCopy,
  onDelete,
  onNavigate,
  onSelectMultiple,
  onToggleSelected,
  reorderEntry,
  section,
}: StandardTocRowProps) {
  const isDraggable = Boolean(reorderEntry);
  const hasSectionActions =
    Boolean(section) &&
    Boolean(onCopy) &&
    Boolean(onDelete) &&
    Boolean(onSelectMultiple);
  const {
    attributes,
    isDragging,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    disabled: !isDraggable || isDragDisabled,
    id: entry.id,
  });
  const rowStyle = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
  };
  const dragTooltip = isDragDisabled
    ? disabledReason ?? 'Save or cancel current edit first'
    : 'Reorder';

  return (
    <li
      className={styles.listItem}
      data-dragging={isDragging ? 'true' : 'false'}
      ref={setNodeRef}
      style={rowStyle}
    >
      <div
        className={styles.navEntryRow}
        data-draggable={isDraggable ? 'true' : 'false'}
      >
        {isSelectionActive && section ? (
          <label className={styles.sectionCheckboxLabel}>
            <input
              checked={isSelected}
              className={styles.sectionCheckbox}
              onChange={onToggleSelected}
              type="checkbox"
            />
            <span className={styles.visuallyHidden}>
              Select section {section.title}
            </span>
          </label>
        ) : isDraggable ? (
          <Tooltip content={dragTooltip}>
            <button
              {...attributes}
              {...listeners}
              aria-label="Reorder"
              className={styles.navDragHandle}
              disabled={isDragDisabled}
              ref={setActivatorNodeRef}
              type="button"
            >
              <GripVertical aria-hidden="true" size={18} strokeWidth={2} />
            </button>
          </Tooltip>
        ) : null}

        <a
          aria-current={isActive ? 'true' : undefined}
          className={styles.entryLink}
          data-active={isActive ? 'true' : 'false'}
          data-level={entry.level}
          data-type={entry.type}
          href={`#${entry.id}`}
          onClick={(event) => {
            event.preventDefault();
            onNavigate(entry.id);
          }}
        >
          {entryLabel}
        </a>

        {hasSectionActions && section ? (
          <span className={styles.navEntryActions}>
            <SectionKebabMenu
              disabled={isActionDisabled || isSelectionActive}
              disabledReason={actionDisabledReason}
              onCopy={onCopy!}
              onDelete={onDelete!}
              onSelectMultiple={onSelectMultiple!}
              sectionTitle={section.title}
            />
          </span>
        ) : null}
      </div>
    </li>
  );
}

export function NavigationPanel({
  courseId,
  courseTitle,
  homeHref,
  homeLabel,
  isOpen,
  navReorder,
  onHomeClick,
  onSelectionActiveChange,
  onToggle,
  sectionActions,
  tocEntries,
}: NavigationPanelProps) {
  const panelId = 'content-navigation-panel';
  const panelRef = useRef<HTMLElement | null>(null);
  const toggleButtonRef = useRef<HTMLButtonElement | null>(null);
  const [isDesktop, setIsDesktop] = useState(isDesktopViewport);
  const navReorderEnabled = Boolean(navReorder?.isEnabled);
  const isSectionActionMode = Boolean(sectionActions) && !navReorderEnabled;
  const sectionAnchorIds = useMemo(
    () =>
      sectionActions?.sections.map((section) =>
        getContentUnitAnchorId(section.headerUnit.id),
      ) ?? [],
    [sectionActions?.sections],
  );
  const activeHeadingIds = useMemo(
    () =>
      isSectionActionMode
        ? sectionAnchorIds
        : tocEntries.map((entry) => entry.id),
    [isSectionActionMode, sectionAnchorIds, tocEntries],
  );
  const activeId = useActiveHeading(activeHeadingIds);
  const sectionSelection = useSectionSelection();
  const sectionDragDisabled =
    sectionActions?.isDragDisabled || sectionSelection.isSelectionActive;
  const sectionDrag = useSectionDragAndDrop({
    disabled: sectionDragDisabled,
    onReorder: (activeSectionId, overSectionId) => {
      sectionActions?.onReorderSection(activeSectionId, overSectionId);
    },
    sections: sectionActions?.sections ?? [],
  });
  const [activeNavEntryId, setActiveNavEntryId] = useState<string | null>(null);
  const navReorderEntryById = useMemo(() => {
    const entries = new Map<string, NavigationPanelNavReorderEntry>();

    navReorder?.sections.forEach((section) => {
      section.entries.forEach((entry) => {
        entries.set(entry.id, entry);
      });
    });

    return entries;
  }, [navReorder?.sections]);
  const sectionActionByEntryId = useMemo(() => {
    const sections = new Map<string, ContentSection>();

    sectionActions?.sections.forEach((section) => {
      sections.set(getContentUnitAnchorId(section.headerUnit.id), section);
    });

    return sections;
  }, [sectionActions?.sections]);
  const navSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    sectionSelection.cancelSelection();
  }, [sectionActions?.selectionResetKey, sectionSelection.cancelSelection]);

  useEffect(() => {
    onSelectionActiveChange?.(sectionSelection.isSelectionActive);
  }, [onSelectionActiveChange, sectionSelection.isSelectionActive]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQueryList = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches);
    };

    setIsDesktop(mediaQueryList.matches);

    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', handleChange);
      return () => {
        mediaQueryList.removeEventListener('change', handleChange);
      };
    }

    mediaQueryList.addListener(handleChange);
    return () => {
      mediaQueryList.removeListener(handleChange);
    };
  }, []);

  useEffect(() => {
    if (isDesktop || !isOpen) {
      return undefined;
    }

    const previousActiveElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panelElement = panelRef.current;
    const focusableElements = getFocusableElements(panelElement);
    const firstElement = focusableElements[0] ?? panelElement;

    firstElement?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onToggle();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const currentFocusableElements = getFocusableElements(panelRef.current);

      if (currentFocusableElements.length === 0) {
        event.preventDefault();
        panelRef.current?.focus();
        return;
      }

      const firstFocusable = currentFocusableElements[0];
      const lastFocusable =
        currentFocusableElements[currentFocusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      } else if (!event.shiftKey && document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      (toggleButtonRef.current ?? previousActiveElement)?.focus();
    };
  }, [isDesktop, isOpen, onToggle]);

  const toggleLabel = isOpen ? 'Collapse navigation' : 'Expand navigation';
  const panelTitle = courseTitle?.trim().length ? courseTitle : 'Table of Contents';
  const resolvedHomeHref = homeHref ?? `/app/courses/${courseId}`;
  const resolvedHomeLabel = homeLabel ?? 'Course Home';
  const ToggleIcon = isOpen ? PanelLeftClose : PanelLeftOpen;

  function renderToggleButton(className: string) {
    return (
      <Tooltip content={toggleLabel}>
        <button
          aria-controls={panelId}
          aria-expanded={isOpen}
          aria-label={toggleLabel}
          className={className}
          onClick={onToggle}
          ref={toggleButtonRef}
          type="button"
        >
          <ToggleIcon aria-hidden="true" className={styles.toggleIcon} size={16} />
        </button>
      </Tooltip>
    );
  }

  function navigateToAnchor(anchorId: string) {
    const target = document.getElementById(anchorId);

    if (target instanceof HTMLElement) {
      scrollToAnchorTarget(target);
    }

    if (!isDesktop && isOpen) {
      onToggle();
    }
  }

  function renderStandardNavigation() {
    if (tocEntries.length === 0) {
      return <div className={styles.emptyState} />;
    }

    if (!navReorderEnabled || !navReorder) {
      return (
        <ol className={styles.list}>
          {tocEntries.map((entry) => {
            const entryLabel = entry.numberedLabel ?? entry.label;
            const isActive = activeId === entry.id;

            return (
              <li className={styles.listItem} key={entry.id}>
                <a
                  aria-current={isActive ? 'true' : undefined}
                  className={styles.entryLink}
                  data-active={isActive ? 'true' : 'false'}
                  data-level={entry.level}
                  data-type={entry.type}
                  href={`#${entry.id}`}
                  onClick={(event) => {
                    event.preventDefault();
                    navigateToAnchor(entry.id);
                  }}
                >
                  {entryLabel}
                </a>
              </li>
            );
          })}
        </ol>
      );
    }

    const chunks: Array<
      | {
          entries: TocEntry[];
          kind: 'sortable';
          sectionId: string;
        }
      | {
          entry: TocEntry;
          kind: 'static';
        }
    > = [];

    tocEntries.forEach((entry) => {
      const reorderEntry = navReorderEntryById.get(entry.id);
      const currentChunk = chunks[chunks.length - 1];

      if (reorderEntry) {
        if (
          currentChunk?.kind === 'sortable' &&
          currentChunk.sectionId === reorderEntry.sectionId
        ) {
          currentChunk.entries.push(entry);
          return;
        }

        chunks.push({
          entries: [entry],
          kind: 'sortable',
          sectionId: reorderEntry.sectionId,
        });
        return;
      }

      chunks.push({
        entry,
        kind: 'static',
      });
    });

    const activeNavEntry = activeNavEntryId
      ? tocEntries.find((entry) => entry.id === activeNavEntryId)
      : null;
    const actionDisabledReason =
      sectionActions?.actionDisabledReason ??
      'Save your changes before copying, deleting, or selecting sections.';

    return (
      <>
        {sectionActions && sectionSelection.isSelectionActive ? (
          <SelectionToolbar
            disabled={sectionActions.isActionDisabled}
            onCancel={sectionSelection.cancelSelection}
            onCopy={() => {
              const selectedSections = getSelectedSections();

              if (selectedSections.length > 0) {
                sectionActions.onCopySections(selectedSections);
              }
            }}
            onDelete={() => {
              const selectedSections = getSelectedSections();

              if (selectedSections.length > 0) {
                sectionActions.onDeleteSections(selectedSections);
              }
            }}
            selectedCount={sectionSelection.selectedCount}
          />
        ) : null}

        {chunks.map((chunk) => {
          if (chunk.kind === 'static') {
            const entryLabel = chunk.entry.numberedLabel ?? chunk.entry.label;
            const isActive = activeId === chunk.entry.id;

            return (
              <ol className={styles.list} key={`static-${chunk.entry.id}`}>
                <li className={styles.listItem}>
                  <a
                    aria-current={isActive ? 'true' : undefined}
                    className={styles.entryLink}
                    data-active={isActive ? 'true' : 'false'}
                    data-level={chunk.entry.level}
                    data-type={chunk.entry.type}
                    href={`#${chunk.entry.id}`}
                    onClick={(event) => {
                      event.preventDefault();
                      navigateToAnchor(chunk.entry.id);
                    }}
                  >
                    {entryLabel}
                  </a>
                </li>
              </ol>
            );
          }

          return (
            <DndContext
              autoScroll
              collisionDetection={closestCenter}
              key={`sortable-${chunk.sectionId}`}
              onDragCancel={() => {
                setActiveNavEntryId(null);
              }}
              onDragEnd={(event: DragEndEvent) => {
                setActiveNavEntryId(null);

                const activeEntry = navReorderEntryById.get(String(event.active.id));
                const overEntry = event.over
                  ? navReorderEntryById.get(String(event.over.id))
                  : undefined;

                if (
                  !activeEntry ||
                  !overEntry ||
                  activeEntry.id === overEntry.id ||
                  activeEntry.sectionId !== chunk.sectionId ||
                  overEntry.sectionId !== chunk.sectionId
                ) {
                  return;
                }

                navReorder.onReorderEntry(
                  chunk.sectionId,
                  activeEntry.unitId,
                  overEntry.unitId,
                );
              }}
              onDragStart={(event) => {
                setActiveNavEntryId(String(event.active.id));
              }}
              sensors={navSensors}
            >
              <SortableContext
                items={chunk.entries.map((entry) => entry.id)}
                strategy={verticalListSortingStrategy}
              >
                <ol className={styles.list}>
                  {chunk.entries.map((entry) => {
                    const section = sectionActionByEntryId.get(entry.id);
                    const isSelectionActiveForEntry =
                      Boolean(section) && sectionSelection.isSelectionActive;
                    const isDragDisabled =
                      Boolean(navReorder.isDragDisabled) ||
                      isSelectionActiveForEntry;

                    return (
                      <StandardTocRow
                        actionDisabledReason={actionDisabledReason}
                        disabledReason={
                          isSelectionActiveForEntry
                            ? 'Exit multi-select to reorder'
                            : navReorder.disabledReason
                        }
                        entry={entry}
                        entryLabel={entry.numberedLabel ?? entry.label}
                        isActionDisabled={Boolean(sectionActions?.isActionDisabled)}
                        isActive={activeId === entry.id}
                        isDragDisabled={isDragDisabled}
                        isSelected={
                          section
                            ? sectionSelection.selectedSectionIdsSet.has(section.id)
                            : false
                        }
                        isSelectionActive={isSelectionActiveForEntry}
                        key={entry.id}
                        onCopy={
                          section && sectionActions
                            ? () => sectionActions.onCopySections([section])
                            : undefined
                        }
                        onDelete={
                          section && sectionActions
                            ? () => sectionActions.onDeleteSections([section])
                            : undefined
                        }
                        onNavigate={navigateToAnchor}
                        onSelectMultiple={
                          section
                            ? () => {
                                if (
                                  sectionActions?.onBeforeSelectMultiple?.() ===
                                  false
                                ) {
                                  return;
                                }

                                sectionSelection.enterSelection(section.id);
                              }
                            : undefined
                        }
                        onToggleSelected={
                          section
                            ? () => sectionSelection.toggleSection(section.id)
                            : undefined
                        }
                        reorderEntry={navReorderEntryById.get(entry.id)}
                        section={section}
                      />
                    );
                  })}
                </ol>
              </SortableContext>
              <DragOverlay>
                {activeNavEntry ? (
                  <div className={styles.navDragPreview}>
                    {activeNavEntry.numberedLabel ?? activeNavEntry.label}
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          );
        })}
      </>
    );
  }

  function getSelectedSections() {
    if (!sectionActions) {
      return [];
    }

    return sectionActions.sections.filter((section) =>
      sectionSelection.selectedSectionIdsSet.has(section.id),
    );
  }

  function renderSectionActionNavigation() {
    if (!sectionActions) {
      return null;
    }

    const actionDisabledReason =
      sectionActions.actionDisabledReason ??
      'Save your changes before copying, deleting, or selecting sections.';
    const activeSection = sectionDrag.activeSectionId
      ? sectionActions.sections.find(
          (section) => section.id === sectionDrag.activeSectionId,
        )
      : null;

    return (
      <div>
        {sectionSelection.isSelectionActive ? (
          <SelectionToolbar
            disabled={sectionActions.isActionDisabled}
            onCancel={sectionSelection.cancelSelection}
            onCopy={() => {
              const selectedSections = getSelectedSections();

              if (selectedSections.length > 0) {
                sectionActions.onCopySections(selectedSections);
              }
            }}
            onDelete={() => {
              const selectedSections = getSelectedSections();

              if (selectedSections.length > 0) {
                sectionActions.onDeleteSections(selectedSections);
              }
            }}
            selectedCount={sectionSelection.selectedCount}
          />
        ) : null}

        {sectionActions.sections.length > 0 ? (
          <DndContext
            collisionDetection={closestCenter}
            onDragCancel={sectionDrag.handleDragCancel}
            onDragEnd={sectionDrag.handleDragEnd}
            onDragStart={sectionDrag.handleDragStart}
            sensors={sectionDrag.sensors}
          >
            <SortableContext
              items={sectionDrag.sectionIds}
              strategy={verticalListSortingStrategy}
            >
              <ol className={styles.sectionActionList}>
                {sectionActions.sections.map((section) => {
                  const anchorId = getContentUnitAnchorId(section.headerUnit.id);
                  const isActive = activeId === anchorId;
                  const isSelected =
                    sectionSelection.selectedSectionIdsSet.has(section.id);

                  return (
                    <SectionTocRow
                      actionDisabledReason={actionDisabledReason}
                      anchorId={anchorId}
                      isActionDisabled={Boolean(sectionActions.isActionDisabled)}
                      isActive={isActive}
                      isDragDisabled={Boolean(sectionDragDisabled)}
                      isSelected={isSelected}
                      isSelectionActive={sectionSelection.isSelectionActive}
                      key={section.id}
                      onCopy={() => sectionActions.onCopySections([section])}
                      onDelete={() => sectionActions.onDeleteSections([section])}
                      onNavigate={navigateToAnchor}
                      onSelectMultiple={() => {
                        if (
                          sectionActions.onBeforeSelectMultiple?.() === false
                        ) {
                          return;
                        }

                        sectionSelection.enterSelection(section.id);
                      }}
                      onToggleSelected={() => {
                        sectionSelection.toggleSection(section.id);
                      }}
                      section={section}
                    />
                  );
                })}
              </ol>
            </SortableContext>
            <DragOverlay>
              {activeSection ? (
                <div className={styles.sectionDragPreview}>
                  <span className={styles.sectionTitle}>
                    {activeSection.title}
                  </span>
                  <span className={styles.sectionMeta}>
                    {getSectionUnitCount(activeSection)} content units
                  </span>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        ) : (
          <p className={styles.sectionEmptyState}>
            No numbered sections yet.
          </p>
        )}
      </div>
    );
  }

  return (
    <div
      className={styles.root}
      data-desktop={isDesktop ? 'true' : 'false'}
      data-open={isOpen ? 'true' : 'false'}
    >
      {!isOpen && !isDesktop ? renderToggleButton(styles.edgeToggle) : null}

      {!isDesktop && isOpen ? (
        <button
          aria-hidden="true"
          className={styles.backdrop}
          onClick={onToggle}
          tabIndex={-1}
          type="button"
        />
      ) : null}

      {/* Collapsed on a small screen the panel sits off the left edge: inert
          keeps Tab and screen readers out of it, as well as the pointer.
          aria-hidden alone left its links in the tab order; it stays for a
          browser without inert. */}
      <aside
        aria-hidden={!isOpen && !isDesktop ? 'true' : undefined}
        className={styles.panel}
        inert={!isOpen && !isDesktop}
        id={panelId}
        ref={panelRef}
        tabIndex={-1}
      >
        <div className={styles.panelContent}>
          <div className={styles.panelHeader}>
            <h2 className={styles.title}>{panelTitle}</h2>
            {isOpen || isDesktop ? renderToggleButton(styles.panelToggle) : null}
          </div>

          <Link
            className={styles.courseHomeLink}
            onClick={() => {
              onHomeClick?.();

              if (!isDesktop && isOpen) {
                onToggle();
              }
            }}
            to={resolvedHomeHref}
          >
            <ArrowLeft aria-hidden="true" size={14} />
            <span>{resolvedHomeLabel}</span>
          </Link>

          <nav aria-label="Table of contents">
            {sectionActions && !navReorderEnabled
              ? renderSectionActionNavigation()
              : renderStandardNavigation()}
          </nav>
        </div>
      </aside>
    </div>
  );
}
